<template>
  <div class="talking-points-resource-manage-ui">
    <EditableList
      :list="talkingPoints"
      :table-headers="tableHeaders"
      :is-loading="isLoading"
      :has-error="hasError"
      :pagination="pagination"
      :variation-class-name="variationClassName"
      :list-item-description="listItemDescription"
      @fetch="fetchTalkingPoints"
      @edit="onEdit"
      @delete="onDelete"
    >
      <template #header>
        <div class="talking-points-resource-manage-ui__header">
          <div class="talking-points-resource-manage-ui__form">
            <TalkingPointResourceForm
              :model="model"
              :patch-data-client="patchDataClient"
              :full-control="fullControl"
              form-feedback-variation-class-name="no-margin"
              date-picker-position="left"
              date-picker-auto-position="bottom"
              @upsert="onAdd"
            />
          </div>
        </div>
      </template>
      <template #row="{ item }">
        <slot name="row" :item="item">
          <EditableListText :is-disabled="isTalkingPointDisabled(item)" class="talking-point">{{
            item.text
          }}</EditableListText>
          <EditableListText
            :is-disabled="isTalkingPointDisabled(item)"
            class="talking-point"
            multi-line
            >{{ patchesString(item.patches) }}</EditableListText
          >
          <EditableListText :is-disabled="isTalkingPointDisabled(item)" class="talking-point">{{
            DateTimeFormatter.toDashedDate(item.start_date)
          }}</EditableListText>
          <EditableListText :is-disabled="isTalkingPointDisabled(item)" class="talking-point">{{
            item.end_date ? DateTimeFormatter.toDashedDate(item.end_date) : 'no end date'
          }}</EditableListText>
          <EditableListText :is-disabled="isTalkingPointDisabled(item)" class="talking-point">{{
            DateTimeFormatter.toDashedDate(item.updated_at ?? '')
          }}</EditableListText>
          <EditableListText variant-class="talking-point">
            <VBtn
              outline
              class="talking-points-resource-manage-ui__status slim"
              title="Toggles if client allowed to use app"
              :disabled="isTalkingPointEndDateInPast(item)"
              @click="toggleIsActive(item)"
            >
              <i
                :class="[
                  'fr',
                  `fr-${item.active ? 'check-circle' : 'cancel'}`,
                  'glyph',
                  `talking-points-resource-manage-ui__status--${
                    item.active ? 'active' : 'suspended'
                  }`,
                ]"
              ></i>
            </VBtn>
          </EditableListText>
        </slot>
      </template>
    </EditableList>
  </div>
</template>

<script setup lang="ts">
  // Types
  import type {
    ITalkingPointsResourceModel,
    TPaginatedTalkingPoints,
    TTalkingPoint,
  } from '~/misc/talking-points-resource/types';
  import type { TUserPatch } from '~/misc/user-resource/types';
  import type { TPagination } from '~/misc/resource/types';
  import type { TUser as TAuthUser } from '~/misc/auth/types';
  import type { TPatch, TPatchDataClient } from '~/misc/patch/types';

  // Services
  import { PaginationCalculator } from '~/misc/pagination/services/pagination-calculator';
  import { PatchLabelResolver } from '~/misc/patch/services/label-resolver';
  import { DateTimeFormatter } from '~/misc/utils/datetime';

  // Composables
  import { useNotifyResource } from '~/misc/resource/composables/use-notify-resource';

  // Components
  import TalkingPointResourceForm from '~/misc/talking-points-resource/manage/form/TalkingPointResourceForm.vue';
  import EditableList from '~/misc/editable-list/EditableList.vue';
  import EditableListText from '~/misc/editable-list/text/EditableListText.vue';
  import VBtn from '~/misc/v-btn';

  const UpdateModal = defineAsyncComponent(
    () =>
      import(
        '~/misc/talking-points-resource/manage/update-modal/TalkingPointResourceUpdateModal.vue'
      )
  );

  const props = defineProps({
    model: {
      type: Object as PropType<ITalkingPointsResourceModel>,
      required: true,
    },
    patchDataClient: {
      type: Object as PropType<TPatchDataClient>,
      required: true,
    },
    authUser: {
      type: Object as PropType<TAuthUser | null>,
      default: null,
    },
    fullControl: {
      type: Boolean,
      default: false,
    },
    variationClassName: {
      type: String,
      default: 'talking-point-resource',
    },
    tableHeaders: {
      type: Array as PropType<string[]>,
      default: () => [
        'Message',
        'Patches',
        'Start Date',
        'End Date',
        'Updated Date',
        'Is active',
        '',
      ],
    },
    listItemDescription: {
      type: String,
      required: false,
      default: 'the talking point entirely',
    },
    refetchTrigger: {
      type: Number,
      default: 0,
    },
  });

  const { error, success } = useToast();
  const { show: showModal } = useModal();
  const { notifySuccess, notifyFailure } = useNotifyResource();

  const talkingPoints: Ref<TTalkingPoint[]> = ref<TTalkingPoint[]>([]);
  const pagination: Ref<TPagination | null> = ref<TPagination | null>(null);
  const isLoading: Ref<boolean> = ref<boolean>(false);
  const hasError: Ref<boolean> = ref<boolean>(false);
  const searchQuery: Ref<string> = ref<string>('');

  const setIsLoading = (state: boolean) => {
    isLoading.value = state;
  };

  const setHasError = (state: boolean) => {
    hasError.value = state;
  };

  const setTalkingPoints = (data: TTalkingPoint[]) => {
    talkingPoints.value = data;
  };

  const setPagination = (data: TPagination | null) => {
    pagination.value = data;
  };

  const fetchTalkingPoints = async (page?: number) => {
    setIsLoading(true);
    setHasError(false);

    try {
      const response: TPaginatedTalkingPoints = await props.model.index({
        page,
        q: searchQuery.value || undefined,
      });

      setTalkingPoints(response.data);
      setPagination(response.meta);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const findTalkingPointIndex = (id: number): number =>
    talkingPoints.value.findIndex(
      (existingTalkingPoint: TTalkingPoint) => existingTalkingPoint.id === id
    );

  const onEdit = (talkingPoint: TTalkingPoint) => {
    showModal(UpdateModal, {
      model: props.model,
      patchDataClient: props.patchDataClient,
      talkingPoint,
      fullControl: props.fullControl,
      updatedCallback: (updatedTalkingPoint: TTalkingPoint) => {
        const index = findTalkingPointIndex(updatedTalkingPoint.id);

        if (index !== -1) {
          talkingPoints.value[index] = updatedTalkingPoint;
        }
      },
    });
  };

  const onAdd = () => fetchTalkingPoints(PaginationCalculator.getPageOnAdd(pagination.value));

  const onDelete = (id: number) => {
    props.model
      .destroy(id)
      .then(() => fetchTalkingPoints(PaginationCalculator.getPageOnDelete(pagination.value)))
      .then(notifySuccess)
      .catch((e) => {
        // TODO: repeated
        if (e?.response?.data.message) {
          notifyFailure(e?.response?.data.message);
        }
      });
  };

  const patchesString = (patches: TUserPatch[]): string =>
    PatchLabelResolver.getListLabel(patches as TPatch[]);

  const isTalkingPointEndDateInPast = (talkingPoint: TTalkingPoint): boolean => {
    if (!talkingPoint.end_date) return false;

    const today: string = DateTimeFormatter.toIsoDate(new Date());
    const endDate: string = DateTimeFormatter.toIsoDate(talkingPoint.end_date);

    return endDate < today;
  };

  const isTalkingPointDisabled = (talkingPoint: TTalkingPoint): boolean => {
    return !talkingPoint.active || isTalkingPointEndDateInPast(talkingPoint);
  };

  const toggleIsActive = (talkingPoint: TTalkingPoint) => {
    (talkingPoint.active
      ? props.model.suspend(talkingPoint.id, {
          ...talkingPoint,
          patches: talkingPoint.patches.map((p: TUserPatch) => p.id),
        })
      : props.model.activate(talkingPoint.id, {
          ...talkingPoint,
          patches: talkingPoint.patches.map((p: TUserPatch) => p.id),
        })
    )
      .then(() => {
        success({ message: 'Done' });
        talkingPoint.active = !talkingPoint.active;
      })
      .catch(() => {
        error({ message: 'Something went wrong' });
      });
  };

  watch(searchQuery, () => fetchTalkingPoints(), { immediate: true });

  watch(
    () => props.refetchTrigger,
    () => fetchTalkingPoints(pagination.value?.current_page)
  );
</script>
